import { getShortformLocale, LocaleCode, LocaleCodeMap } from '@doss/common-all'
import { createContext, lazy, useContext, useEffect, useMemo } from 'react'
import { apolloClient } from '../apollo-client'
import {
  ApplicationOrientation,
  Currency,
  GetSimpleUserQuery,
  Locale,
  RoleType,
  useGetSimpleUserQuery,
} from '../generated/apollo-operations'
import { auth } from '../lib/firebase'
import { useAuthProviderType } from '../utils/auth-provider'
import { migrateUserLocalStorage } from '../utils/migrate-local-storage'
import { UserPermissionsManager } from '../utils/user-permissions-manager'
import { useAuth } from './useAuth'

const ResponsiveBar = lazy(() => import('@nivo/bar').then((m) => ({ default: m.ResponsiveBar })))
const ResponsiveLine = lazy(() => import('@nivo/line').then((m) => ({ default: m.ResponsiveLine })))
const ResponsivePie = lazy(() => import('@nivo/pie').then((m) => ({ default: m.ResponsivePie })))
const ResponsiveScatterPlot = lazy(() =>
  import('@nivo/scatterplot').then((m) => ({
    default: m.ResponsiveScatterPlot,
  })),
)
const ResponsiveRadialBar = lazy(() =>
  import('@nivo/radial-bar').then((m) => ({ default: m.ResponsiveRadialBar })),
)

export interface UserInfo {
  id: string
  firstName: string
  lastName: string
  email: string | null
  phoneNumber: string | null
  policiesAcceptedAt: string | null
  orientation: ApplicationOrientation
  organizationId: string
  organizationName: string
  organizationLogo: {
    id: string
    name: string
    storageUrl: string
  } | null
  organizationLocale: Locale
  organizationCurrency: Currency
  organizationTimeZone: string
  organizationVersion: string | null
  // Org feature flags; keys are OrganizationFeatureFlag values, missing means disabled.
  organizationFeatureFlags: Record<string, unknown>
  profilePicture: GetSimpleUserQuery['currentUser']['profilePicture']
  defaultSignatureFileUrl?: string
}

export type GlobalInfo = {
  user?: UserInfo
  locale: LocaleCode
  shortformLocale: string
  currency: Currency
  isLoading: boolean
  isInternal: boolean
  isAdmin: boolean
  permissionsManager: UserPermissionsManager
  ResponsiveBar: typeof ResponsiveBar
  ResponsiveLine: typeof ResponsiveLine
  ResponsivePie: typeof ResponsivePie
  ResponsiveScatterPlot: typeof ResponsiveScatterPlot
  ResponsiveRadialBar: typeof ResponsiveRadialBar
}

const GlobalContext = createContext<GlobalInfo>({
  locale: LocaleCode.EN_US,
  shortformLocale: 'en',
  currency: Currency.USD,
  isLoading: true,
  isInternal: false,
  isAdmin: false,
  permissionsManager: new UserPermissionsManager([]),
  ResponsiveBar,
  ResponsiveLine,
  ResponsivePie,
  ResponsiveScatterPlot,
  ResponsiveRadialBar,
})

/**
 * AuthProvider is a wrapper component that provides the user's authentication information to the rest of the app.
 * Uses unified auth hook that handles both WorkOS and Firebase (legacy).
 */
export function GlobalContextProvider({ children }: { children: React.ReactNode }) {
  // Unified authentication (handles both WorkOS and Firebase)
  const { isLoading: loadingAuth, isSignedIn: isAuthenticated, user } = useAuth()
  const { isWorkOS: isWorkOSProvider } = useAuthProviderType()

  const {
    data,
    loading: loadingUser,
    error: errorUser,
    refetch: refetchCurrentUser,
  } = useGetSimpleUserQuery({
    skip: !isAuthenticated,
    fetchPolicy: 'network-only',
  })

  // Also listen for Firebase token changes (legacy support)
  useEffect(() => {
    return auth.onIdTokenChanged((firebaseUser) => {
      if (firebaseUser) {
        refetchCurrentUser().catch(() => {
          // Apollo surfaces the error via the hook's `error` — no action needed here.
        })
      }
    })
  }, [refetchCurrentUser])

  // Set up authentication listener for cross-tab logout
  useEffect(() => {
    // Listen for logout events from other tabs (based on active auth provider)
    const handleStorageChange = async (event: StorageEvent) => {
      if (isWorkOSProvider) {
        // For WorkOS, listen to the actual event WorkOSAuthContext emits
        if (event.key === 'workos_auth_event' && event.newValue) {
          try {
            const authEvent = JSON.parse(event.newValue)
            if (authEvent.type === 'signout') {
              // Clear Apollo cache to prevent stale data on next login
              await Promise.all([apolloClient.clearStore(), auth.signOut()])
            }
          } catch {
            // Ignore parse errors
          }
        }
      } else {
        // For Firebase, listen to firebase_logout_event
        if (event.key === 'firebase_logout_event' && event.newValue) {
          // Clear Apollo cache to prevent stale data on next login
          await Promise.all([apolloClient.clearStore(), auth.signOut()])
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [isWorkOSProvider])

  useEffect(() => {
    if (isAuthenticated) {
      // Clean up logout event markers on successful authentication
      if (isWorkOSProvider) {
        localStorage.removeItem('workos_auth_event')
      } else {
        localStorage.removeItem('firebase_logout_event')
      }
    }
  }, [isAuthenticated, isWorkOSProvider])

  // Migrate localStorage data when user ID changes (Firebase ID → UUID migration)
  useEffect(() => {
    if (data?.currentUser.id) {
      migrateUserLocalStorage(data.currentUser.id)
    }
  }, [data?.currentUser.id])

  // Stay "loading" while Firebase has an authenticated user but the currentUser
  // query hasn't resolved yet. Without this, AuthGuard can see
  // { user: undefined, isLoading: false } in the render window between Firebase
  // sign-in and the GraphQL `me` response and bounce the user back to /auth/login.
  // On query error we fall through so the guard can still redirect to login.
  const loading = loadingAuth || loadingUser || (!!user && !data && !errorUser)

  const contextValue = useMemo(() => {
    if (!data) {
      return {
        user: undefined,
        locale: LocaleCode.EN_US,
        shortformLocale: 'en',
        currency: Currency.USD,
        isLoading: loading,
        isInternal: false,
        isAdmin: false,
        permissionsManager: new UserPermissionsManager([]),
      }
    }

    return {
      user: {
        id: data.currentUser.id,
        firstName: data.currentUser.firstName,
        lastName: data.currentUser.lastName,
        email: data.currentUser.email,
        phoneNumber: data.currentUser.phoneNumber,
        policiesAcceptedAt: data.currentUser.policiesAcceptedAt,
        orientation: data.currentUser.orientation,
        organizationId: data.currentUser.organization.id,
        organizationName: data.currentUser.organization.name,
        organizationLogo: data.currentUser.organization.logo,
        organizationLocale: data.currentUser.organization.locale,
        organizationCurrency: data.currentUser.organization.currency,
        organizationTimeZone: data.currentUser.organization.timeZone,
        organizationVersion: data.currentUser.organization.version,
        // featureFlags is a nullable JSONObject: an org with no flags configured has it as
        // null, so coalesce to {} to keep the non-nullable Record contract consumers rely on.
        organizationFeatureFlags: (data.currentUser.organization.featureFlags ?? {}) as Record<
          string,
          unknown
        >,
        defaultSignatureFileUrl: data.currentUser.defaultSignature?.signatureFile.storageUrl,
        profilePicture: data.currentUser.profilePicture,
      },
      locale: LocaleCodeMap[data.currentUser.organization.locale],
      shortformLocale: getShortformLocale(data.currentUser.organization.locale),
      currency: data.currentUser.organization.currency,
      isLoading: loading,
      isInternal: data.currentUser.roles.some((role) => role.roleType === RoleType.INTERNAL_DOSS),
      isAdmin: data.currentUser.roles.some((role) => role.roleType === RoleType.ADMIN),
      permissionsManager: new UserPermissionsManager(data.currentUser.roles),
    }
  }, [data, loading])

  return (
    <GlobalContext.Provider
      value={{
        ...contextValue,
        ResponsiveBar,
        ResponsiveLine,
        ResponsivePie,
        ResponsiveScatterPlot,
        ResponsiveRadialBar,
      }}
    >
      {children}
    </GlobalContext.Provider>
  )
}

export function useGlobalContext() {
  return useContext(GlobalContext)
}
